import React, { useRef, useEffect, useLayoutEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cn } from '../../../../../lib/utils';
import { getCategoryStyles } from '../../../../orderhistory/categoryUtils';
import { formatCategoryName } from '../../../../orderhistory/sections/FilterControls';
import { useAppSettingsStore } from '../../../../../stores/appSettingsStore';

export interface FilterOption {
  value: string;
  label: string;
  /** Dimmed second line under the label (e.g. an email). Also searched on. */
  sublabel?: string;
}

interface FilterDropdownProps {
  /** Fallback label when nothing (or "all") is selected. */
  label: string;
  /** Leading glyph in the trigger. Rendered only when passed. */
  icon?: React.ReactNode;
  options: FilterOption[];
  /** Single-select value. Ignored in multi-select mode. */
  value: string;
  /** Single-select handler. Ignored in multi-select mode. */
  onChange: (value: string) => void;
  /** Prefer anchoring the menu to the right edge of the trigger. */
  alignRight?: boolean;
  actions?: { label: string; onClick: () => void }[];
  /** Show an internal search box (auto-on when there are many options). */
  searchable?: boolean;
  /** Translation key for the search box placeholder. Defaults to a plain "Search". */
  searchPlaceholder?: string;
  /** Inline trigger: no pill/border/height, just the label + a small chevron. */
  compact?: boolean;
  /** Extra classes on the trigger button. In compact mode the label inherits its color. */
  triggerClassName?: string;
  /** 'category' renders each option as a coloured category badge (like the table column filters). */
  valueType?: 'default' | 'category';
  /** Stretch trigger (and menu) to fill the parent width. */
  fullWidth?: boolean;
  className?: string;

  // ---- Multi-select mode ----
  // Passing both of these switches the dropdown to multi-select: options render
  // checkboxes, picking one keeps the menu open, and `value`/`onChange` go unused.
  /** Currently selected values. Empty array means "no filter applied". */
  selectedValues?: string[];
  onSelectionChange?: (values: string[]) => void;
  /** Renders a "Select all" shortcut in the menu header. */
  onSelectAll?: () => void;
  /** Renders a "Clear all" shortcut in the menu header. */
  onClearAll?: () => void;
  /** Plural noun for the trigger once 2+ options are selected, e.g. "3 restaurants". */
  countNoun?: string;
}

const MENU_WIDTH = 240;
const VIEWPORT_PAD = 8;

/**
 * Filter dropdown shared by the Product Utilization report and the Staff page.
 * Pill trigger (optional icon + label + chevron) opening a portaled menu with
 * optional search, scrollable options and optional actions (e.g. Export CSV).
 * `valueType='category'` renders options as coloured category badges, matching the
 * table column filters.
 *
 * Single-select closes on pick; passing `selectedValues` + `onSelectionChange`
 * switches to multi-select, which toggles checkboxes and keeps the menu open.
 *
 * The menu is portaled to <body> so it escapes ancestor `overflow:hidden` clipping,
 * and is measured against the trigger each time it opens: it flips to the trigger's
 * right edge when there is more room that side, then clamps into the viewport.
 */
export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  icon,
  options,
  value,
  onChange,
  alignRight = false,
  actions,
  searchable,
  searchPlaceholder,
  compact = false,
  triggerClassName,
  valueType = 'default',
  fullWidth = false,
  className,
  selectedValues,
  onSelectionChange,
  onSelectAll,
  onClearAll,
  countNoun,
}) => {
  const { t } = useTranslation();
  const darkMode = useAppSettingsStore((s) => s.darkMode);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const multi = !!selectedValues && !!onSelectionChange;

  const showSearch = searchable ?? options.length > 8;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q === ''
      ? options
      : options.filter(
          (o) =>
            o.label.toLowerCase().includes(q) || (o.sublabel ?? '').toLowerCase().includes(q) || o.value === 'all'
        );
  }, [options, query]);

  // 'all' is a reset sentinel, never a selection worth highlighting.
  const activeValues = useMemo(
    () => (multi ? selectedValues ?? [] : [value]).filter((v) => v && v !== 'all'),
    [multi, selectedValues, value]
  );
  const activeSet = useMemo(() => new Set(activeValues), [activeValues]);
  const hasSelection = activeValues.length > 0;

  // Both modes name a single selection; multi collapses 2+ into "N <countNoun>".
  const triggerLabel = useMemo(() => {
    if (!hasSelection) return label;
    if (activeValues.length === 1) {
      return options.find((o) => o.value === activeValues[0])?.label ?? label;
    }
    return countNoun ? `${activeValues.length} ${countNoun}` : label;
  }, [hasSelection, activeValues, options, label, countNoun]);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;

    const update = () => {
      const rect = buttonRef.current!.getBoundingClientRect();
      const width = fullWidth ? Math.max(rect.width, MENU_WIDTH) : MENU_WIDTH;
      const spaceRight = window.innerWidth - rect.left - VIEWPORT_PAD;
      const spaceLeft = rect.right - VIEWPORT_PAD;

      const placeRight = alignRight || (spaceRight < width && spaceLeft >= width) || spaceRight < spaceLeft;

      let left = placeRight ? rect.right - width : rect.left;
      left = Math.max(VIEWPORT_PAD, Math.min(left, window.innerWidth - width - VIEWPORT_PAD));

      setMenuPos({ top: rect.bottom + 4, left, width });
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, alignRight, fullWidth]);

  useEffect(() => {
    if (!open) return;
    // The menu is portaled out of this subtree, so "outside" has to be measured
    // against both the menu and the trigger.
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
      setQuery('');
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSelect = (optValue: string) => {
    if (multi) {
      const current = selectedValues ?? [];
      onSelectionChange!(current.includes(optValue) ? current.filter((v) => v !== optValue) : [...current, optValue]);
      return;
    }
    onChange(optValue);
    setOpen(false);
    setQuery('');
  };

  // Mirrors the shortcut the staff filters had before this became shared: offer
  // "Select all" only while nothing is picked, "Clear all" once something is.
  const showSelectAll = multi && !!onSelectAll && !hasSelection;
  const showClearAll = multi && !!onClearAll && hasSelection;

  return (
    <div className={cn('relative', fullWidth && 'w-full', className)}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center gap-2 transition-colors whitespace-nowrap',
          compact
            ? 'gap-1 max-w-full'
            : cn(
                'h-11 px-3 rounded-xl border shadow-sm',
                'bg-background dark:bg-background-dark border-border dark:border-border-dark hover:bg-backgroundDarker dark:hover:bg-backgroundDarker-dark',
                hasSelection && 'border-[#0E8965] text-[#0E8965] dark:border-[#0E8965] dark:text-[#0E8965]'
              ),
          fullWidth && 'w-full justify-between',
          triggerClassName
        )}
      >
        <span className="flex items-center gap-2 min-w-0">
          {icon && <span className="flex-shrink-0 text-muted dark:text-muted-dark">{icon}</span>}
          <span
            className={cn(
              'truncate',
              // Compact keeps the caller's own type + color (it sits inline in a row).
              compact ? 'text-left' : 'text-sm font-semibold',
              fullWidth ? 'min-w-0 flex-1 text-left' : 'max-w-[140px]',
              !compact &&
                (hasSelection ? 'text-[#0E8965] dark:text-[#0E8965]' : 'text-textSecondary dark:text-textSecondary-dark')
            )}
          >
            {triggerLabel}
          </span>
          {/* Only when the label cannot already carry the count. */}
          {multi && activeValues.length > 1 && !countNoun && (
            <span className="min-w-[20px] h-5 px-1 rounded-full bg-[#0E8965]/15 dark:bg-[#0E8965]/15 text-[#0E8965] dark:text-[#0E8965] text-xs font-semibold flex items-center justify-center flex-shrink-0">
              {activeValues.length}
            </span>
          )}
        </span>
        <ChevronDown
          className={cn(
            'shrink-0 transition-transform',
            compact ? 'w-3 h-3' : 'w-4 h-4 text-muted dark:text-muted-dark',
            open && 'rotate-180'
          )}
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className={cn(
              'fixed z-[300] rounded-xl border p-2 flex flex-col shadow-lg',
              'bg-background dark:bg-background-dark border-border dark:border-border-dark',
              !menuPos && 'invisible'
            )}
            style={
              menuPos ? { top: menuPos.top, left: menuPos.left, width: menuPos.width } : { top: 0, left: 0, width: MENU_WIDTH }
            }
          >
            {(showSelectAll || showClearAll) && (
              <div className="flex items-center justify-end px-1 pb-2">
                <button
                  type="button"
                  onClick={() => (showSelectAll ? onSelectAll!() : onClearAll!())}
                  className="text-xs font-medium text-[#0E8965] dark:text-[#0E8965] hover:underline"
                >
                  {showSelectAll ? t('filter.selectAll') : t('filter.clearAll')}
                </button>
              </div>
            )}

            {showSearch && (
              <div className="relative mb-2">
                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted dark:text-muted-dark" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t(searchPlaceholder ?? 'reports.utilization.filter.search')}
                  className="w-full h-9 pl-8 pr-2 rounded-lg text-sm bg-background dark:bg-background-dark border border-border dark:border-border-dark text-textPrimary dark:text-textPrimary-dark placeholder:text-muted dark:placeholder:text-muted-dark focus:outline-none"
                />
              </div>
            )}

            <div className="flex flex-col gap-1 max-h-[240px] overflow-y-auto custom-scrollbar">
              {filtered.length === 0 ? (
                <p className="text-xs text-muted dark:text-muted-dark px-2 py-2">
                  {t('reports.utilization.filter.noMatches')}
                </p>
              ) : (
                filtered.map((opt) => {
                  const active = multi ? activeSet.has(opt.value) : opt.value === value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleSelect(opt.value)}
                      className={cn(
                        'w-full px-2 py-1.5 text-left text-sm rounded-lg transition-colors flex items-center justify-start gap-1.5',
                        active ? 'bg-[#0E8965]/10' : 'hover:bg-backgroundDarker dark:hover:bg-backgroundDarker-dark'
                      )}
                    >
                      {multi ? (
                        <span
                          className={cn(
                            'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0',
                            active
                              ? 'bg-[#0E8965] dark:bg-[#0E8965] border-[#0E8965] dark:border-[#0E8965]'
                              : 'bg-background dark:bg-background-dark border-border dark:border-border-dark'
                          )}
                        >
                          {active && <Check className="w-3 h-3 text-white dark:text-white" strokeWidth={3} />}
                        </span>
                      ) : active ? (
                        <Check className="w-4 h-4 text-[#0E8965] dark:text-[#0E8965] shrink-0" />
                      ) : (
                        <span className="w-4 shrink-0" />
                      )}
                      {valueType === 'category' && opt.value !== 'all' ? (
                        <span
                          className="text-xs font-medium rounded-md px-1.5 py-0.5 truncate"
                          style={getCategoryStyles(opt.value, darkMode)}
                        >
                          {formatCategoryName(opt.value)}
                        </span>
                      ) : (
                        <span className="min-w-0 flex flex-col">
                          <span
                            className={cn(
                              'truncate',
                              active
                                ? 'text-[#0E8965] dark:text-[#0E8965] font-medium'
                                : 'text-textSecondary dark:text-textSecondary-dark'
                            )}
                          >
                            {opt.label}
                          </span>
                          {opt.sublabel && (
                            <span className="truncate text-xs text-muted dark:text-muted-dark">{opt.sublabel}</span>
                          )}
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>

            {actions && actions.length > 0 && (
              <>
                {options.length > 0 && <div className="h-px bg-border dark:bg-border-dark my-1" />}
                {actions.map((a) => (
                  <button
                    key={a.label}
                    type="button"
                    onClick={() => {
                      a.onClick();
                      setOpen(false);
                    }}
                    className="w-full px-2 py-1.5 text-left text-sm text-textSecondary dark:text-textSecondary-dark hover:bg-backgroundDarker dark:hover:bg-backgroundDarker-dark rounded-lg transition-colors"
                  >
                    {a.label}
                  </button>
                ))}
              </>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default FilterDropdown;
